import { roundNumberAndDecimals } from '@teinor/erp/prices'
import { totalCalculateParams } from '@teinor/erp/prices/quote/calculate'
import { operationTypesInversed } from '@teinor/erp/types/company/cashRegister/cashRegisterMoney/cashRegisterMoneyLog'
import { PaymentTypeModelAdd } from '@teinor/erp/types/company/config/paymentType'
import { Fragment, useCallback, useState } from 'react'
import { SwitchButton, TabItems } from '../../../../../../baseComponents/Button/SwitchButton'
import { DisplayPriceGettingParams } from '../../../../../../baseComponents/Displays/DisplayPrice'
import PermissionChecker from '../../../../../../baseComponents/PermissionChecker'
import TableWithoutVirtualListing from '../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { getISOCurrencyMain, getUserPermissions } from '../../../../../../utils/reducers/getters'
import { PaymentTypeModelTypeExtendedTPV } from '../../types'
import CashReghisterMoneyLogNameBoxTPV from '../CashRegisterMoneyLogNameBoxTPV'
import CustomInputTotalsTablePriceTPV from '../CustomInputTotalsTable'
import PaymentMethodBoxTPV from './PaymentMethodBoxTPV'

interface PaymentTypesAndSellsRegisterTPVProps {
    paymentTypesTPV: PaymentTypeModelTypeExtendedTPV[] | null
}

const PaymentTypesAndSellsRegisterTPV: React.FC<PaymentTypesAndSellsRegisterTPVProps> = ({ paymentTypesTPV }) => {
    const { activeTab, changeTab } = usePaymentTypesAndSellsRegisterTPVLogic()
    const userPermissions = getUserPermissions('sellOther.seeAllCashSells')
    const {t} = useTwinTranslation()
    const items: TabItems[] = [
        {
            element: <TwinTrans transKey={'sells'}>Ventas</TwinTrans>,
            extraProps: {title: t('paymentMethodSells', 'Ventas por métodos de pago')}
        },
        {
            element: <TwinTrans transKey={'totals'}>Totales</TwinTrans>,
            extraProps: { title: t('paymentMethodRegister', 'Registro de métodos de pago')}
        },
    ]

    return (
        <Fragment>
            <PermissionChecker userPermissions={userPermissions} permission='create'>
                <div className='switch_buttons payment_types_register_switch flex items-center px-30 mt-10 mb-20 w-full '>
                    <h2>{items?.[activeTab]?.extraProps?.title}</h2>
                    <SwitchButton className='w-fit ml-auto' items={items} onClick={changeTab} active={activeTab} />
                </div>
            </PermissionChecker>
            {activeTab === 0 ?
                <PaymentSellsRegisterTPV paymentTypesTPV={paymentTypesTPV} /> :
                <PaymentTypesRegisterTPV paymentTypesTPV={paymentTypesTPV} />
            }
        </Fragment>
    )
}

const usePaymentTypesAndSellsRegisterTPVLogic = () => {
    const [activeTab, setActiveTab] = useState<number>(0)
    const changeTab = useCallback((val: number) => {
        setActiveTab(val)
    }, [setActiveTab])
    return { activeTab, changeTab }
}


interface PaymentTypesRegisterTPVProps {
    paymentTypesTPV: PaymentTypeModelTypeExtendedTPV[] | null
}

const PaymentTypesRegisterTPV: React.FC<PaymentTypesRegisterTPVProps> = ({ paymentTypesTPV }) => {
    const mainCurrencyId = getISOCurrencyMain()
    const renderThis: JSX.Element[] = []
    let totalPaymentTypes = 0
    for (const element of paymentTypesTPV || []) {
        const myCurrency = element.extraData?.CurrencyId || mainCurrencyId
        let myTotal = 0
        for (const log of element?.CashRegisterMoney?.CashRegisterMoneyLogs || []) {
            let myMoney = parseFloat(String(log.money)) || 0
            if (parseInt(String(log.operationType)) === operationTypesInversed['cashOpening']) {
                myMoney = parseFloat(String(log.endMoney)) || 0
            }
            myTotal = myTotal + myMoney
        }
        totalPaymentTypes = roundNumberAndDecimals( totalPaymentTypes + myTotal, totalCalculateParams)
        renderThis.push(<PaymentMethodBoxTPV element={element} CurrencyId={myCurrency} total={myTotal} />)
    }

    return (
        <Fragment>
            <div className='payment_types_register_tpv px-30 flex flex-auto flex-col overflow-hidden'>
                <div className='flex flex-auto flex-col gap-y-20 overflow-auto'>
                    {renderThis}
                </div>
            </div>
            <TotalPaymentTypes CurrencyId={mainCurrencyId} total={totalPaymentTypes} />
        </Fragment>
    )
}

interface TotalPaymentTypesProps {
    CurrencyId: string
    total: number
}

const TotalPaymentTypes: React.FC<TotalPaymentTypesProps> = ({ total, CurrencyId }) => {
    const { t } = useTwinTranslation()
    return (
        <div className='flex mt-20 bg-gray-FB payment_footer '>
            <div className='ml-auto'>
                <CustomInputTotalsTablePriceTPV currency={CurrencyId} value={parseFloat(String(total))} className='medium18 ' classNameValue={'regular22 '} inputName={t('totalCap', 'TOTAL') + ':'} />
            </div>
        </div>
    )
}

interface PaymentSellsRegisterTPVProps {
    paymentTypesTPV: PaymentTypeModelTypeExtendedTPV[] | null
}

interface PaymentTypeWithSellsProps extends PaymentTypeModelAdd {
    myTotalSells: number
    myTotalRefund: number
    myTotal: number
    myQuantity: number
    CurrencyId: string
}

const PaymentSellsRegisterTPV: React.FC<PaymentSellsRegisterTPVProps> = ({ paymentTypesTPV }) => {
    const { t } = useTwinTranslation()
    const mainCurrencyId = getISOCurrencyMain()
    const dictSellsPaymentMethod: PaymentTypeWithSellsProps[] = []
    let totalPayment = 0
    let totalPaymentSells = 0
    let totalPaymentRefund = 0
    let totalQuantity = 0
    for (const element of paymentTypesTPV || []) {
        let myTotalSells = 0
        let myTotalRefund = 0
        let myTotal = 0
        let myQuantity = 0
        for (const log of element.CashRegisterMoney?.CashRegisterMoneyLogs || []) {
            const myMoney = parseFloat(String(log?.money)) || 0
            if (log.operationType === operationTypesInversed['ticket']) {
                myTotalSells = myTotalSells + myMoney
                myTotal = myTotal + myMoney
                totalQuantity++
                myQuantity++
            } else if (log.operationType === operationTypesInversed['ticketRefund']) {
                myTotalRefund = myTotalRefund + myMoney
                myTotal = myTotal + myMoney
            }
        }
        dictSellsPaymentMethod.push({ id: element.id, name: element.name, icon: element.icon, CurrencyId: element.extraData?.CurrencyId || mainCurrencyId, myTotalSells, myTotalRefund, myTotal, myQuantity })
        totalPayment = totalPayment + myTotal
        totalPaymentSells = totalPaymentSells + myTotalSells
        totalPaymentRefund = totalPaymentRefund + myTotalRefund
    }

    const columns: ColumnTableSchema<PaymentTypeWithSellsProps> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('paymentMethod', 'Método de pago'),
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: paymentTypesTPV || {}, label: t('paymentMethod', 'Método de pago') }
            },
            searchKey: 'CashRegisterMoney.PaymentType.name',
            searchSingleKey: 'CashRegisterMoney.PaymentType.id',
            customRender: (parameterValue, allRowData) => <CashReghisterMoneyLogNameBoxTPV icon={allRowData?.icon || ''} name={allRowData?.name || ''} />
        },
        {
            id: 'myQuantity',
            dataKey: 'myQuantity',
            label: t('tickets', 'Tickets'),
            width: 80,
            className: 'justify-end',
            headerClassName: 'justify-end'
        },
        {
            id: 'myTotalSells',
            dataKey: 'myTotalSells',
            label: t('sells', 'Ventas'),
            width: 120,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={false} CurrencyId={allRowData?.CurrencyId} />,
            className: 'justify-end',
            headerClassName: 'justify-end'
        },
        {
            id: 'myTotalRefund',
            dataKey: 'myTotalRefund',
            label: t('Refunds', 'Devoluciones'),
            width: 120,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={false} CurrencyId={allRowData?.CurrencyId} />,
            className: 'justify-end',
            headerClassName: 'justify-end'
        },
        {
            id: 'myTotal',
            dataKey: 'myTotal',
            label: t('total', 'Total'),
            width: 120,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={false} CurrencyId={allRowData?.CurrencyId} />,
            className: 'payment_method_sells justify-end',
            headerClassName: 'justify-end'
        },
    ]
    return (
        <Fragment>
            <div className='payment_types_register_tpv px-30 flex flex-auto flex-col overflow-hidden'>
                <div className='flex-auto table_container overflow-auto'>
                    <TableWithoutVirtualListing data={dictSellsPaymentMethod} columns={columns} disableScroll={true} className='payment_method_sells' />
                </div>
            </div>
            <TotalPaymentSells CurrencyId={mainCurrencyId} total={totalPayment} totalRefund={totalPaymentRefund} totalSells={totalPaymentSells} totalQuantity={totalQuantity} />
        </Fragment>
    )
}

interface TotalPaymentSellsProps {
    CurrencyId: string
    total: number
    totalSells: number
    totalRefund: number
    totalQuantity: number
}

const TotalPaymentSells: React.FC<TotalPaymentSellsProps> = ({ total, totalSells, totalRefund, totalQuantity, CurrencyId }) => {
    const { t } = useTwinTranslation()
    return (
        <div className='flex px-30 py-20 payment_sells_totals bg-gray-FB'>
            <div className='medium18 mr-20 ml-auto items-center flex'>{t('totalCap', 'TOTAL') + ':'}</div>
            <span className='medium18 w-80 flex justify-end px-8 items-center ' >{parseFloat(String(totalQuantity))}</span>
            <DisplayPriceGettingParams value={parseFloat(String(totalSells))} with_vat={false} CurrencyId={CurrencyId} className='medium18 w-120 flex justify-end px-8 items-center ' />
            <DisplayPriceGettingParams value={parseFloat(String(totalRefund))} with_vat={false} CurrencyId={CurrencyId} className='medium18 w-120 flex justify-end px-8 items-center ' />
            <DisplayPriceGettingParams value={parseFloat(String(total))} with_vat={false} CurrencyId={CurrencyId} className='medium18 w-120 flex justify-end px-8 items-center ' />
        </div>
    )
}

export default PaymentTypesAndSellsRegisterTPV